/**
 * One ticket photo in the activity stream: a thumbnail that opens full size.
 *
 * The bytes are fetched with the session token (fetchTicketPhotoUrl) because
 * an <img src> can't send it; the object URL is revoked on unmount.
 */
import React, { useEffect, useState } from "react";

import Modal from "../Modal";
import { Button } from "../ui/button";
import { fetchTicketPhotoUrl, type TicketPhoto } from "../../services/ticketsApi";

interface TicketPhotoThumbProps {
  origin: "source" | "_jcox";
  ticketId: number;
  photo: TicketPhoto;
  /** Shown only when set (uploader or admin). */
  onDelete?: () => Promise<void>;
}

export const TicketPhotoThumb: React.FC<TicketPhotoThumbProps> = ({
  origin,
  ticketId,
  photo,
  onDelete,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    fetchTicketPhotoUrl(origin, ticketId, photo.photo_id)
      .then((u) => {
        created = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [origin, ticketId, photo.photo_id]);

  const remove = async (): Promise<void> => {
    if (!onDelete || !window.confirm("Remove this photo from the ticket?")) return;
    setBusy(true);
    try {
      await onDelete();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const alt = photo.caption || "Ticket photo";

  return (
    <>
      <button
        type="button"
        onClick={() => url && setOpen(true)}
        className="w-24 h-24 rounded border border-secondary-200 bg-secondary-50 overflow-hidden flex items-center justify-center"
        title={photo.caption ?? "View photo"}
        disabled={!url}
      >
        {url ? (
          <img src={url} alt={alt} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[10px] text-secondary-500 px-1 text-center">
            {failed ? "photo unavailable" : "loading…"}
          </span>
        )}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={photo.caption || "Photo"}
        widthClass="max-w-4xl"
      >
        {url && <img src={url} alt={alt} className="max-w-full max-h-[75vh] mx-auto rounded" />}
        <div className="mt-3 flex items-center justify-between text-xs text-secondary-500">
          <span>
            Uploaded by {photo.uploaded_by}
            {photo.node_id && <> · step {photo.node_id}</>}
          </span>
          {onDelete && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void remove()}
              className="text-error-700 border-error-300"
            >
              {busy ? "Removing…" : "Remove photo"}
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
};

export default TicketPhotoThumb;
